import React from "react";
import {
  Box,
  Button,
  IconButton as MuiIconButton,
  InputBase,
  Typography
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import InboxIcon from "@mui/icons-material/Inbox";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import adminTokens from "../styles/adminDesignTokens";

export const PageHeader = ({ eyebrow, title, description, actions }) => (
  <Box className="garahex-ui-pageHeader">
    <Box>
      {eyebrow ? <div className="garahex-ui-eyebrow">{eyebrow}</div> : null}
      <h1 className="garahex-ui-title">{title}</h1>
      {description ? <p className="garahex-ui-description">{description}</p> : null}
    </Box>
    {actions ? <Box sx={{ display: "flex", gap: 0.8, flexWrap: "wrap" }}>{actions}</Box> : null}
  </Box>
);

export const SectionHeader = ({ title, description, actions }) => (
  <Box className="garahex-ui-sectionHeader">
    <Box>
      <Typography component="h2">{title}</Typography>
      {description ? (
        <Typography sx={{ mt: 0.35, color: adminTokens.colors.muted, fontSize: adminTokens.density.fontBody }}>
          {description}
        </Typography>
      ) : null}
    </Box>
    {actions ? <Box sx={{ display: "flex", gap: 0.8, flexWrap: "wrap" }}>{actions}</Box> : null}
  </Box>
);

export const ActionButton = ({ children, sx, ...props }) => (
  <Button
    className="garahex-ui-actionButton"
    variant="contained"
    sx={{
      background: `linear-gradient(135deg, ${adminTokens.colors.garahexOrange}, ${adminTokens.colors.warmOrange})`,
      color: "#fff",
      "&:hover": {
        background: `linear-gradient(135deg, ${adminTokens.colors.warmOrange}, ${adminTokens.colors.garahexOrange})`
      },
      ...sx
    }}
    {...props}
  >
    {children}
  </Button>
);

export const AdminIconButton = ({ children, label, ...props }) => (
  <MuiIconButton className="garahex-headerIconButton" aria-label={label} {...props}>
    {children}
  </MuiIconButton>
);

export const StatusBadge = ({ children, tone = "info" }) => {
  const colorMap = {
    success: adminTokens.colors.success,
    warning: adminTokens.colors.warning,
    danger: adminTokens.colors.danger,
    purple: adminTokens.colors.purple,
    info: adminTokens.colors.royalBlue
  };
  const color = colorMap[tone] || colorMap.info;

  return (
    <span className="garahex-ui-statusBadge" style={{ color, backgroundColor: `${color}14` }}>
      {children}
    </span>
  );
};

export const SurfaceCard = ({ children, sx, ...props }) => (
  <Box className="garahex-ui-surfaceCard" sx={sx} {...props}>
    {children}
  </Box>
);

export const SettingCard = ({ children, sx, ...props }) => (
  <Box className="garahex-ui-settingCard" sx={sx} {...props}>
    {children}
  </Box>
);

export const SearchField = ({ placeholder = "Search", value, onChange, onClear, inputProps, ...props }) => (
  <Box className="garahex-searchField" {...props}>
    <SearchIcon fontSize="small" aria-hidden="true" />
    <InputBase
      fullWidth
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      inputProps={{ "aria-label": placeholder, ...inputProps }}
      sx={{ color: "inherit", fontSize: "0.92rem" }}
    />
    {value && onClear ? (
      <MuiIconButton size="small" aria-label="Clear search" onClick={onClear}>
        <ClearIcon fontSize="small" />
      </MuiIconButton>
    ) : null}
  </Box>
);

export const FilterButton = ({ children, ...props }) => (
  <Button variant="outlined" sx={{ borderRadius: 999, textTransform: "none", fontWeight: 800 }} {...props}>
    {children}
  </Button>
);

export const EmptyState = ({ title = "No records yet", description, icon }) => (
  <Box className="garahex-ui-emptyState">
    <Box>
      {icon || <InboxIcon sx={{ fontSize: 42, color: adminTokens.colors.royalBlue, mb: 1 }} />}
      <Typography component="h2" sx={{ fontWeight: 850, color: adminTokens.colors.text }}>
        {title}
      </Typography>
      {description ? <Typography sx={{ mt: 0.7, maxWidth: 440, fontSize: adminTokens.density.fontBody }}>{description}</Typography> : null}
    </Box>
  </Box>
);

export const ErrorState = ({ title = "Unable to load data", description, action }) => (
  <Box className="garahex-ui-emptyState" role="alert" aria-live="polite">
    <Box>
      <ErrorOutlineIcon sx={{ fontSize: 44, color: adminTokens.colors.danger, mb: 1 }} />
      <Typography component="h2" sx={{ fontWeight: 900, color: adminTokens.colors.text }}>
        {title}
      </Typography>
      {description ? <Typography sx={{ mt: 0.7, maxWidth: 520, fontSize: adminTokens.density.fontBody }}>{description}</Typography> : null}
      {action ? <Box sx={{ mt: 1.4 }}>{action}</Box> : null}
    </Box>
  </Box>
);

export const LoadingSkeleton = ({ height = 120, sx }) => (
  <Box className="garahex-ui-loadingSkeleton" sx={{ height, ...sx }} aria-hidden="true" />
);

export const TableSkeleton = ({ rows = 5 }) => (
  <Box sx={{ display: "grid", gap: 0.8 }}>
    {Array.from({ length: rows }).map((_, index) => (
      <LoadingSkeleton key={index} height={42} />
    ))}
  </Box>
);

export const FormSkeleton = ({ fields = 4 }) => (
  <Box sx={{ display: "grid", gap: 1 }}>
    {Array.from({ length: fields }).map((_, index) => (
      <LoadingSkeleton key={index} height={index === fields - 1 ? 72 : 42} />
    ))}
  </Box>
);

export const ModalSurface = ({ children, sx, ...props }) => (
  <Box className="garahex-ui-modalSurface" sx={{ p: 2, ...sx }} {...props}>
    {children}
  </Box>
);
